/**
 * Offset-aware weave generation in the spirit of Broken Twill:
 * checker ground, short floats that jog the diagonal on a repeating cycle
 * so the cloth reads as a clean repeat — with enough layout knobs for variety.
 */

/** Row layout matching PatternOffset bead numbering. */
export function buildOffsetRows(cols, rows) {
  const width = Math.max(1, Math.floor(Number(cols)) || 1);
  const height = Math.max(1, Math.floor(Number(rows)) || 1);
  const gridTotal = width * height;
  const total = Math.ceil(width * height + height / 2);
  let counter = total + 1;
  const rowsOut = [];

  for (let i = height - 1; i >= 0; i -= 1) {
    const num = width + (i % 2 === 0 ? 1 : 0);
    const beads = [];
    for (let j = 0; j < num; j += 1) {
      counter -= 1;
      beads.unshift(counter);
    }
    const nth = height - i;
    const shifted = nth % 2 === 0;
    const r = nth - 1;
    rowsOut.push({
      shifted,
      r,
      beads: beads.map((bn, k) => ({ bn, k })),
    });
  }

  return { cols: width, rows: height, gridTotal, rowsOut };
}

export function checkerRow(n, start) {
  const a = start === "B" ? "B" : "A";
  const b = a === "A" ? "B" : "A";
  return Array.from({ length: n }, (_, i) => (i % 2 === 0 ? a : b));
}

/**
 * Place a same-color float of `span` beads at `pos`, then flip phase after.
 * span defaults to 2 (Broken-Twill-style jog).
 */
export function applyFloat(row, pos, span = 2) {
  const next = row.slice();
  const n = next.length;
  const s = Math.max(2, Math.min(Math.floor(span) || 2, n));
  if (pos < 0 || pos > n - s) return next;
  for (let k = 1; k < s; k += 1) {
    next[pos + k] = next[pos];
  }
  for (let j = pos + s; j < n; j += 1) {
    next[j] = next[j] === "B" ? "A" : "B";
  }
  return next;
}

function randInt(rng, min, max) {
  return min + Math.floor(rng() * (max - min + 1));
}

function pick(rng, list) {
  return list[randInt(rng, 0, list.length - 1)];
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x || 1;
}

/** Keep wave length even so float path and A/B start phase repeat together. */
export function ensureEvenPeriod(wave) {
  if (wave.length === 0) return [0, 0];
  if (wave.length % 2 === 0) return wave;
  return [...wave, wave[0]];
}

function clampWave(wave, maxPos) {
  return wave.map((p) => Math.max(0, Math.min(maxPos, p)));
}

/** Structural repeat length for unshifted (and paired shifted) rows. */
export function structuralPeriod(waveLen, floatEvery, shear) {
  const len = Math.max(1, waveLen);
  const every = Math.max(1, floatEvery);
  const step = Math.max(1, shear % len || 1);
  const cycle = len / gcd(step, len);
  let period = every * cycle;
  if (period % 2 === 1) period *= 2;
  return period;
}

/**
 * Companion path for the other row in each pair — related to the primary
 * wave so the cloth stays coherent.
 */
export function deriveCompanionWave(wave, maxPos, mode) {
  if (!wave.length || mode === "none") return null;
  if (mode === "mirror") {
    return wave.map((p) => maxPos - p);
  }
  if (mode === "lag") {
    const half = Math.floor(wave.length / 2);
    return wave.map((_, i) => wave[(i + half) % wave.length]);
  }
  if (mode === "oppose") {
    // Half a beat behind and mirrored — a counter-diagonal.
    const half = Math.floor(wave.length / 2);
    return wave.map((_, i) => maxPos - wave[(i + half) % wave.length]);
  }
  if (mode === "center") {
    const mid = maxPos / 2;
    return wave.map((p) => Math.round(mid - (p - mid)));
  }
  return null;
}

/**
 * Short, periodic float paths. Each Generate picks a shape, band, and
 * direction; the path then loops so the tube shows a clean repeat.
 */
export function buildCleanWave(rng, maxPos) {
  if (maxPos <= 0) return { wave: [0, 0], kind: "rise", amp: 0, base: 0 };

  // Float in a band that may be narrower than the full width, and may sit
  // left / center / right — same cloth logic, different diagonal travel.
  const amp =
    maxPos <= 1 ? maxPos : randInt(rng, Math.max(1, Math.ceil(maxPos / 2)), maxPos);
  const base = randInt(rng, 0, maxPos - amp);

  const kind = pick(rng, [
    "triangle",
    "rise",
    "fall",
    "step",
    "valley",
    "plateau",
    "pulse",
    "stairs",
    "twin",
    "saw",
    "broken",
  ]);

  let local;
  if (kind === "triangle") {
    local = [];
    for (let p = 0; p <= amp; p += 1) local.push(p);
    for (let p = amp - 1; p >= 1; p -= 1) local.push(p);
  } else if (kind === "rise") {
    local = Array.from({ length: amp + 1 }, (_, i) => i);
  } else if (kind === "fall") {
    local = Array.from({ length: amp + 1 }, (_, i) => amp - i);
  } else if (kind === "step") {
    const step =
      amp >= 3 ? pick(rng, [1, 2, Math.max(1, Math.floor(amp / 2))]) : 1;
    local = Array.from({ length: amp + 1 }, (_, i) => (i * step) % (amp + 1));
  } else if (kind === "valley") {
    local = [];
    for (let p = amp; p >= 0; p -= 1) local.push(p);
    for (let p = 1; p <= amp; p += 1) local.push(p);
  } else if (kind === "plateau") {
    local = [];
    for (let p = 0; p <= amp; p += 1) local.push(p);
    local.push(amp);
    for (let p = amp - 1; p >= 1; p -= 1) local.push(p);
  } else if (kind === "pulse") {
    local =
      amp >= 2
        ? [0, Math.floor(amp / 2), amp, Math.floor(amp / 2)]
        : [0, 0, amp, amp];
  } else if (kind === "stairs") {
    // Linger on each tread before climbing.
    local = [];
    for (let p = 0; p <= amp; p += 1) {
      local.push(p, p);
    }
  } else if (kind === "twin") {
    // Two peaks inside the band — a figure-eight diagonal.
    const lo = Math.floor(amp / 3);
    const hi = Math.max(lo + 1, amp - lo);
    local = [lo, hi, lo, hi, Math.floor((lo + hi) / 2), hi, lo, Math.floor((lo + hi) / 2)];
  } else if (kind === "saw") {
    // Asymmetric: slow climb, hard drop.
    local = [];
    for (let p = 0; p <= amp; p += 1) local.push(p);
    local.push(0);
  } else {
    // "broken": library Broken Twill wave, scaled into the band.
    const up = [];
    for (let p = 1; p <= amp; p += 1) up.push(p);
    const bounce = amp >= 2 ? Math.max(1, amp - 2) : 0;
    local = [...up, bounce, 0];
  }

  let wave = ensureEvenPeriod(clampWave(local.map((p) => base + p), maxPos));
  if (rng() < 0.5) wave = wave.slice().reverse();

  // Occasional stretch: hold each step twice for a slower diagonal.
  if (wave.length <= 8 && rng() < 0.22) {
    wave = ensureEvenPeriod(wave.flatMap((p) => [p, p]));
  }

  return { wave, kind, amp, base };
}

/**
 * Generate an A/B letter grid (length cols*rows) using periodic weave logic.
 *
 * @param {{ cols: number, rows: number, rng?: () => number }} opts
 * @returns {{ letters: ("A"|"B")[], meta: object }}
 */
export function generateWeaveLetters({ cols, rows, rng = Math.random }) {
  const { gridTotal, rowsOut } = buildOffsetRows(cols, rows);
  const width = Math.max(1, Math.floor(Number(cols)) || 1);
  const maxPos = Math.max(0, width - 2);
  const { wave, kind, amp, base } = buildCleanWave(rng, maxPos);
  const phase = wave.length ? randInt(rng, 0, wave.length - 1) : 0;
  const shear = wave.length >= 4 && rng() < 0.35 ? 2 : 1;
  const floatSpan =
    width >= 6 && maxPos >= 3 && rng() < 0.45
      ? 3
      : width >= 8 && maxPos >= 4 && rng() < 0.15
        ? 4
        : 2;
  const floatEvery =
    wave.length >= 2 && rng() < 0.35 ? pick(rng, [2, 3]) : 1;
  const startPolarity = rng() < 0.5 ? "B" : "A";
  const invertRoles = rng() < 0.5;
  // Which row of the pair carries the primary jog.
  const primaryOn = pick(rng, ["unshifted", "unshifted", "shifted", "both"]);
  const companionMode =
    primaryOn === "both"
      ? pick(rng, ["mirror", "lag", "oppose", "center"])
      : rng() < 0.4
        ? pick(rng, ["mirror", "lag", "oppose"])
        : "none";
  const companion = deriveCompanionWave(wave, maxPos, companionMode);
  const period = structuralPeriod(wave.length, floatEvery, shear);
  const flip = (letter) => {
    if (!invertRoles) return letter;
    return letter === "A" ? "B" : "A";
  };
  const other = (letter) => (letter === "A" ? "B" : "A");

  const letters = Array(gridTotal).fill("A");

  const wavePosAt = (idx) => {
    if (idx % floatEvery !== 0) return null;
    const waveIdx = Math.floor(idx / floatEvery);
    return wave[(waveIdx * shear + phase) % wave.length] ?? 0;
  };

  const companionPosAt = (idx) => {
    if (!companion || idx % floatEvery !== 0) return null;
    const waveIdx = Math.floor(idx / floatEvery);
    return companion[(waveIdx * shear + phase) % companion.length] ?? 0;
  };

  for (const row of rowsOut) {
    const widthHere = row.beads.length;
    const idx = Math.floor(row.r / 2);
    const start = idx % 2 === 0 ? startPolarity : other(startPolarity);
    const maxFloatPos = Math.max(0, widthHere - floatSpan);
    let rowLetters = checkerRow(widthHere, start);

    // Primary row of the pair rides `wave`; the other may echo via companion.
    let floatPos = null;
    if (row.shifted) {
      if (primaryOn === "shifted") floatPos = wavePosAt(idx);
      else if (primaryOn === "both") floatPos = companionPosAt(idx) ?? wavePosAt(idx);
      else floatPos = companionPosAt(idx);
    } else if (primaryOn === "unshifted" || primaryOn === "both") {
      floatPos = wavePosAt(idx);
    } else {
      floatPos = companionPosAt(idx);
    }

    if (floatPos != null) {
      rowLetters = applyFloat(
        rowLetters,
        Math.min(floatPos, maxFloatPos),
        floatSpan
      );
    }

    for (const { bn, k } of row.beads) {
      if (bn < 1 || bn > gridTotal) continue;
      const letter = rowLetters[Math.min(k, rowLetters.length - 1)] || "A";
      letters[bn - 1] = flip(letter);
    }
  }

  return {
    letters,
    meta: {
      style: kind,
      wave,
      companion,
      companionMode,
      amp,
      base,
      phase,
      shear,
      floatSpan,
      floatEvery,
      primaryOn,
      startPolarity,
      invertRoles,
      period,
    },
  };
}

/**
 * Build a small A/B motif block meant to tile cleanly across the chart.
 */
export function buildMotifBlock(rng, mw, mh) {
  const w = Math.max(2, Math.floor(mw) || 2);
  const h = Math.max(2, Math.floor(mh) || 2);
  const kind = pick(rng, [
    "hstripe",
    "vstripe",
    "checker",
    "blocks",
    "diagonal",
    "chevron",
    "steps",
    "frame",
    "diamond",
  ]);
  const invert = rng() < 0.5;
  const paint = (on) => {
    const letter = on ? "B" : "A";
    return invert ? (letter === "A" ? "B" : "A") : letter;
  };

  const motif = Array.from({ length: h }, () => Array(w).fill("A"));

  if (kind === "hstripe") {
    const thick = h >= 4 && rng() < 0.5 ? 2 : 1;
    for (let r = 0; r < h; r += 1) {
      const on = Math.floor(r / thick) % 2 === 0;
      for (let c = 0; c < w; c += 1) motif[r][c] = paint(on);
    }
  } else if (kind === "vstripe") {
    const thick = w >= 4 && rng() < 0.5 ? 2 : 1;
    for (let r = 0; r < h; r += 1) {
      for (let c = 0; c < w; c += 1) {
        motif[r][c] = paint(Math.floor(c / thick) % 2 === 0);
      }
    }
  } else if (kind === "checker") {
    for (let r = 0; r < h; r += 1) {
      for (let c = 0; c < w; c += 1) {
        motif[r][c] = paint((r + c) % 2 === 0);
      }
    }
  } else if (kind === "blocks") {
    const bw = Math.max(1, Math.ceil(w / 2));
    const bh = Math.max(1, Math.ceil(h / 2));
    for (let r = 0; r < h; r += 1) {
      for (let c = 0; c < w; c += 1) {
        motif[r][c] = paint(
          (Math.floor(r / bh) + Math.floor(c / bw)) % 2 === 0
        );
      }
    }
  } else if (kind === "diagonal") {
    const thick = pick(rng, [1, 2, 3]);
    for (let r = 0; r < h; r += 1) {
      for (let c = 0; c < w; c += 1) {
        motif[r][c] = paint(Math.floor((r + c) / thick) % 2 === 0);
      }
    }
  } else if (kind === "chevron") {
    for (let r = 0; r < h; r += 1) {
      for (let c = 0; c < w; c += 1) {
        const v = Math.abs(c - Math.floor((w - 1) / 2)) + r;
        motif[r][c] = paint(v % 2 === 0);
      }
    }
  } else if (kind === "steps") {
    for (let r = 0; r < h; r += 1) {
      const cut = r % w;
      for (let c = 0; c < w; c += 1) {
        motif[r][c] = paint(c <= cut);
      }
    }
  } else if (kind === "frame") {
    for (let r = 0; r < h; r += 1) {
      for (let c = 0; c < w; c += 1) {
        const edge = r === 0 || c === 0 || r === h - 1 || c === w - 1;
        motif[r][c] = paint(edge);
      }
    }
  } else {
    // diamond: Manhattan distance rings from center
    const cy = (h - 1) / 2;
    const cx = (w - 1) / 2;
    for (let r = 0; r < h; r += 1) {
      for (let c = 0; c < w; c += 1) {
        const d = Math.abs(r - cy) + Math.abs(c - cx);
        motif[r][c] = paint(Math.floor(d) % 2 === 0);
      }
    }
  }

  return { motif, kind };
}

/**
 * Tile a small motif across the rectangular chart grid.
 */
export function generateMotifLetters({ cols, rows, rng = Math.random }) {
  const width = Math.max(1, Math.floor(Number(cols)) || 1);
  const height = Math.max(1, Math.floor(Number(rows)) || 1);
  const mw = randInt(rng, 2, Math.min(6, width));
  const mh = randInt(rng, 2, Math.min(8, Math.max(2, Math.floor(height / 2) || 2)));
  const { motif, kind } = buildMotifBlock(rng, mw, mh);
  // Occasional mirror/flip of the tile for more layouts from the same shapes.
  let tile = motif.map((row) => row.slice());
  if (rng() < 0.5) tile = tile.map((row) => row.slice().reverse());
  if (rng() < 0.5) tile = tile.slice().reverse();

  const letters = Array(width * height).fill("A");
  for (let i = 0; i < letters.length; i += 1) {
    const c = i % width;
    const r = Math.floor(i / width);
    letters[i] = tile[r % tile.length][c % tile[0].length];
  }

  return {
    letters,
    meta: {
      style: "motif",
      motifKind: kind,
      motifW: tile[0].length,
      motifH: tile.length,
      motif: tile,
      period: tile.length,
    },
  };
}

export function mapWeaveLettersToColors(letters, colorA, colorB) {
  const a = colorA || "#000000";
  const b = colorB || "#ffffff";
  return letters.map((letter) => (letter === "B" ? b : a));
}
